import { useState, useEffect, useRef, useCallback } from 'react';
import { transcriptionAPI } from '../services/api';
import {
  parseTranscriptionRecord,
  parseDurationToSeconds,
  getRawDuration,
} from '../utils/transcriptionMeta';
import { isProcessingStatus, PROCESSING_STATUSES } from '../utils/transcriptionStatus';
import { useNotification } from '../contexts/NotificationContext';

const processTranscriptionList = (rawList, persistentDurations) => {
  const list = Array.isArray(rawList) ? rawList : [];

  return list.map((item) => {
    const parsedDuration = parseDurationToSeconds(getRawDuration(item));
    const parsed = parseTranscriptionRecord(item);
    const storedDuration = persistentDurations.current[parsed.id];
    const finalDuration =
      parsedDuration || parsed.duration || storedDuration || 0;

    if (finalDuration > 0) {
      persistentDurations.current[parsed.id] = finalDuration;
    }

    return {
      ...parsed,
      duration: finalDuration,
    };
  });
};

const useDashboardTranscriptions = () => {
  const { showSuccessNotification } = useNotification();
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [lastUploadTime, setLastUploadTime] = useState(null);
  const persistentDurations = useRef({});

  const fetchTranscriptions = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);

      const response = await Promise.race([
        transcriptionAPI.getTranscriptions({ page: 1, per_page: 1000 }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        ),
      ]);

      const raw = response.data?.data || response.data || [];
      const processed = processTranscriptionList(raw, persistentDurations);

      setTranscriptions((prev) => {
        return processed.map((newItem) => {
          const existing = prev.find((p) => p.id === newItem.id);
          if ((!newItem.duration || newItem.duration === 0) && existing?.duration) {
            return { ...newItem, duration: existing.duration };
          }
          return newItem;
        });
      });
    } catch (err) {
      if (
        (err.code === 'ECONNABORTED' || err.message.includes('timeout')) &&
        retryCount < 2
      ) {
        setTimeout(() => fetchTranscriptions(retryCount + 1), 2000);
        return;
      }

      if (!err.response && retryCount < 1) {
        setTimeout(() => fetchTranscriptions(retryCount + 1), 3000);
        return;
      }

      setError(
        `Failed to load transcriptions: ${err.response?.data?.message || err.message}`
      );
      setTranscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMissingDurations = useCallback(async (items) => {
    const missing = items.filter(
      (item) => item.status === 'completed' && (!item.duration || item.duration === 0)
    );

    for (const item of missing) {
      try {
        const response = await transcriptionAPI.getTranscription(item.id);
        const detail = response.data?.data || response.data;
        if (detail?.duration && detail.duration > 0) {
          persistentDurations.current[item.id] = detail.duration;
          setTranscriptions((prev) =>
            prev.map((t) =>
              t.id === item.id ? { ...t, duration: detail.duration } : t
            )
          );
        }
      } catch {
        // ignore per-item failures
      }
    }
  }, []);

  const addPendingSession = useCallback((session) => {
    if (!session) return;
    const row = {
      id: session.id || session.session_id || session.upload_id || `pending-${Date.now()}`,
      session_id: session.session_id || session.id || session.upload_id,
      title: session.title || session.case_reference || 'Untitled session',
      case_reference: session.case_reference || session.case_number || '',
      status: 'uploading',
      created_at: session.created_at || new Date().toISOString(),
      language: 'English',
      transcribed_content: '',
    };
    setPendingSessions((prev) => [row, ...prev.filter((item) => item.id !== row.id)]);
    setShouldPoll(true);
  }, []);

  const refreshTranscriptions = useCallback(async () => {
    try {
      const response = await transcriptionAPI.getTranscriptions({
        page: 1,
        per_page: 1000,
      });
      const raw = response.data?.data || response.data || [];
      const processed = processTranscriptionList(raw, persistentDurations);

      setTranscriptions((prev) => {
        processed.forEach((updated) => {
          const previous = prev.find((p) => p.id === updated.id);
          if (
            previous &&
            isProcessingStatus(previous.status) &&
            updated.status === 'completed'
          ) {
            showSuccessNotification(
              'Transcription Complete',
              `${updated.title || 'Your transcription'} has finished processing and is ready!`,
              5000
            );
          }
        });
        return processed;
      });

      return processed;
    } catch {
      return null;
    }
  }, [showSuccessNotification]);

  useEffect(() => {
    fetchTranscriptions();
  }, [fetchTranscriptions]);

  useEffect(() => {
    if (transcriptions.length === 0) return undefined;
    const timer = setTimeout(() => fetchMissingDurations(transcriptions), 2000);
    return () => clearTimeout(timer);
  }, [transcriptions.length, fetchMissingDurations, transcriptions]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (transcriptions.length > 0) {
        fetchMissingDurations(transcriptions);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [transcriptions, fetchMissingDurations]);

  useEffect(() => {
    const hasProcessing = transcriptions.some((item) =>
      isProcessingStatus(item.status)
    );
    setShouldPoll((prev) => {
      if (hasProcessing && !prev) return true;
      if (!hasProcessing && prev) return false;
      return prev;
    });
  }, [transcriptions]);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    const pollInterval = setInterval(async () => {
      const updated = await refreshTranscriptions();
      if (!updated) return;

      const stillProcessing = updated.some((item) => isProcessingStatus(item.status));
      if (!stillProcessing) {
        setShouldPoll(false);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [shouldPoll, refreshTranscriptions]);

  useEffect(() => {
    if (!lastUploadTime) return undefined;

    const timer = setTimeout(async () => {
      await refreshTranscriptions();
      setShouldPoll(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [lastUploadTime, refreshTranscriptions]);

  return {
    transcriptions: [
      ...pendingSessions.filter(
        (pending) => !transcriptions.some(
          (item) =>
            item.id === pending.id
            || item.session_id === pending.session_id
            || (pending.case_reference && item.case_reference === pending.case_reference)
        )
      ),
      ...transcriptions,
    ],
    loading,
    error,
    refetch: fetchTranscriptions,
    refreshTranscriptions,
    lastUploadTime,
    setLastUploadTime,
    addPendingSession,
    processingStatuses: PROCESSING_STATUSES,
  };
};

export default useDashboardTranscriptions;
